/**
 * Who arrived, and which assistant sent them.
 *
 * The one figure this screen exists for is the AI share: of everyone who came,
 * how many were sent by an answer. Everything else on the page breaks that
 * down. The share needs both numerators, which is why the endpoint returns the
 * whole-site totals as well as the assistant ones — a percentage of an unknown
 * denominator is a number nobody can act on.
 */

import { NativeSelect, Tabs, TabsContent, TabsList, TabsTrigger } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api, type TrafficOverview } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useTabParam } from "~/core/hooks";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section, Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { Attribution } from "~/modules/analytics/pages/tracking/attribution";
import { Journeys } from "~/modules/analytics/pages/tracking/journeys";
import {
	count,
	deviceLabel,
	engineLabel,
	rate,
	referrals,
	Slices,
	SourceNote,
	seconds,
	siteSetup,
	TrafficEmpty,
	Unavailable,
} from "~/modules/analytics/pages/tracking/shared";

const TABS = ["traffic", "paths", "attribution"] as const;

/**
 * AI referrals: who arrived, where they went next, and which assistant gets
 * the credit for a conversion. Three tabs of one screen because they are three
 * cuts of the same visits under the same window; the goals they count against
 * are set up in Settings › Site setup.
 */
export function ReferralsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const [tab, setTab] = useTabParam(TABS);

	return (
		<StageShell
			entityId={entityId}
			reading="referrals"
			lede={t(tab === "paths" ? "journeys.lede" : tab === "attribution" ? "attribution.lede" : "traffic.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Tabs value={tab} onValueChange={setTab} className="analytics-tabs">
				<TabsList aria-label={t("reading.referrals")}>
					{TABS.map((id) => (
						<TabsTrigger key={id} value={id}>
							{t(`referrals.tab.${id}`)}
						</TabsTrigger>
					))}
				</TabsList>
				<TabsContent value={tab}>
					{tab === "paths" ? (
						<Journeys entityId={entityId} range={filters.range} />
					) : tab === "attribution" ? (
						<Attribution entityId={entityId} range={filters.range} />
					) : (
						<Traffic entityId={entityId} range={filters.range} />
					)}
				</TabsContent>
			</Tabs>
		</StageShell>
	);
}

function Traffic({ entityId, range }: { entityId: string; range: string }) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", range],
		queryFn: () => api.traffic(entityId, { range }),
		...read,
	});

	return (
		<Async state={state}>
			{(data) => (
				<>
					<SourceNote frame={data} />
					{!data.available || data.setup_required ? (
						<Unavailable frame={data} entityId={entityId} />
					) : data.all.sessions === 0 ? (
						<TrafficEmpty title="traffic.periodEmpty" body="traffic.periodEmptyBody" entityId={entityId} />
					) : (
						<>
							<div className="stats insight-stats">
								<Stat
									label={t("traffic.sessions")}
									hint={t("hint.trafficSessions")}
									value={fmt.number(locale, data.totals.sessions)}
									foot={before(locale, data.totals.sessions, data.before?.sessions)}
								/>
								<Stat
									label={t("traffic.share")}
									hint={t("hint.trafficShare")}
									value={share(locale, data)}
									foot={t("traffic.shareFoot", {
										all: fmt.number(locale, data.all.sessions),
									})}
								/>
								<Stat
									label={t("traffic.users")}
									hint={t("hint.trafficUsers")}
									value={count(locale, data.totals.active_users)}
									foot={before(locale, data.totals.active_users, data.before?.active_users)}
								/>
								<Stat
									label={t("traffic.engagementRate")}
									hint={t("hint.trafficEngagement")}
									value={rate(locale, data.totals.engagement_rate)}
									foot={t("traffic.avgTime", { time: seconds(data.totals.avg_engagement_time) })}
								/>
								<Stat
									label={t("traffic.conversions")}
									hint={t("hint.trafficConversions")}
									value={fmt.number(locale, data.totals.conversions)}
								/>
							</div>

							{data.totals.sessions === 0 ? (
								<TrafficEmpty title="traffic.noAi" body="traffic.noAiBody" entityId={entityId} destination="live" />
							) : null}
							{/* Under three days the chart is one full-width bar
								    restating a figure already in the cards above,
								    which is what a brand sees on the day it installs
								    the snippet. */}
							{data.series.length >= 3 ? (
								<Section title={t("traffic.daily")} lede={t("traffic.dailyHint")}>
									<Days series={data.series} />
								</Section>
							) : null}

							<Breakdown data={data} />
							<div className="row-actions mt-(--s-8)">
								<Link className="drill" to={referrals(entityId, "paths")}>
									{t("traffic.explorePaths")}
								</Link>
								<Link className="drill" to={siteSetup(entityId, "goals")}>
									{t("traffic.measureOutcomes")}
								</Link>
							</div>
						</>
					)}
				</>
			)}
		</Async>
	);
}

const BREAKDOWNS = [
	{ key: "engines", title: "traffic.byEngine", label: "traffic.engine" },
	{ key: "landing", title: "traffic.byLanding", label: "traffic.landing" },
	{ key: "countries", title: "traffic.byPlace", label: "traffic.country" },
	{ key: "devices", title: "traffic.byDevice", label: "traffic.device" },
	{ key: "referrers", title: "traffic.byReferrer", label: "traffic.referrer" },
	{ key: "campaigns", title: "traffic.byCampaign", label: "traffic.campaign" },
] as const;
function Breakdown({ data }: { data: TrafficOverview }) {
	const t = useT();
	const { locale } = useI18n();
	const [choice, setChoice] = useState<string | null>(null);
	const selected =
		BREAKDOWNS.find((item) => item.key === (choice ?? (data.totals.sessions === 0 ? "referrers" : "engines"))) ??
		BREAKDOWNS[0];
	return (
		<Section
			title={t(selected.title)}
			action={
				<NativeSelect
					value={selected.key}
					aria-label={t("traffic.breakdown")}
					onChange={(event) => setChoice(event.target.value)}
				>
					{BREAKDOWNS.map((item) => (
						<option key={item.key} value={item.key}>
							{t(item.title)}
						</option>
					))}
				</NativeSelect>
			}
		>
			<p className="hint mb-(--s-6)">
				{t(selected.key === "referrers" ? "traffic.allSourcesScope" : "traffic.aiScope")}
			</p>
			<Slices
				rows={data[selected.key]}
				label={t(selected.label)}
				render={(key) =>
					selected.key === "engines"
						? engineLabel(t, key)
						: selected.key === "countries"
							? fmt.region(locale, key)
							: selected.key === "devices"
								? deviceLabel(t, key)
								: key
				}
			/>
		</Section>
	);
}

/**
 * The share of referrals that were assistants.
 *
 * Computed here from two counts rather than returned as a rate: a stored
 * percentage cannot be re-bucketed, and the two counts are on the screen
 * beside it so the reader can check it.
 */
function share(locale: Parameters<typeof fmt.percent>[0], data: TrafficOverview): string {
	if (data.all.sessions === 0) return "—";
	return fmt.percent(locale, (data.totals.sessions / data.all.sessions) * 100, 1);
}

function before(locale: Parameters<typeof fmt.number>[0], now: number | null, previous: number | null | undefined) {
	if (now === null || previous === null || previous === undefined) return null;
	return <span className="faint">{fmt.signed(locale, now - previous)}</span>;
}

/**
 * A day per column.
 *
 * The same shape as the crawler chart on the publishing stage, deliberately:
 * those two series answer the halves of one question — an engine came to read
 * the page, and then somebody arrived from it — and a reader comparing them
 * should not also be comparing two chart idioms.
 */
function Days({ series }: { series: TrafficOverview["series"] }) {
	const { locale } = useI18n();
	const tallest = Math.max(1, ...series.map((point) => point.sessions));
	return (
		<div className="days">
			{series.map((point) => (
				<div className="day" key={point.day} title={`${point.day} · ${fmt.number(locale, point.sessions)}`}>
					<div className="day-bar" style={{ height: `${(point.sessions / tallest) * 100}%` }}>
						<span className="day-part day-sessions" style={{ height: "100%" }} />
					</div>
				</div>
			))}
		</div>
	);
}
